import { useEffect, useMemo, useState } from 'react';
import { useApiCall } from '../apiCall';
import { useAuth0 } from '@auth0/auth0-react';
import { LogoService } from 'src/api/Logo';
import { DEPARTMENT_BASE_PATH } from 'src/routes';
import { useDepartment } from '../department';
import { useLogoUpdate } from './useLogoUpdate';
import { useLocation, useParams } from 'react-router';

const useLogo = (): {
    logoUrl: string | null;
    isLoadingLogo: boolean;
    error: string | undefined;
} => {
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const { getAccessTokenSilently } = useAuth0();
    const { logoOrgId } = useLogoUpdate();

    const { pathname } = useLocation();

    const { departmentId: departmentIdParam } = useParams<{
        departmentId: string;
    }>();

    const departmentId = useMemo(
        () =>
            departmentIdParam ??
            pathname.split(`${DEPARTMENT_BASE_PATH}/`)?.[1]?.split('/')?.[0],
        [departmentIdParam, pathname],
    );

    const { department, fetchDepartment } = useDepartment(departmentId);

    useEffect(() => {
        fetchDepartment();
    }, [departmentId, fetchDepartment]);

    const orgId = useMemo(() => {
        if (!department?.org?.id) {
            return logoOrgId;
        }

        return department.org.id;
    }, [department, logoOrgId]);

    const {
        response,
        isLoading: isLoadingLogo,
        error,
        requestApiCall: fetchLogo,
    } = useApiCall(
        async (orgIdParam?: string) => {
            const token = await getAccessTokenSilently();
            if (!token) {
                throw new Error('No token available');
            }
            if (!orgIdParam) {
                throw new Error('No organization available');
            }

            return LogoService.getLogo(token, orgIdParam);
        },
        [orgId],
    );

    useEffect(() => {
        fetchLogo();
    }, [orgId, fetchLogo]);

    useEffect(() => {
        if (response?.data) {
            const url = URL.createObjectURL(response.data);
            setLogoUrl(url);

            return (): void => URL.revokeObjectURL(url);
        }
    }, [response]);

    return { logoUrl, isLoadingLogo, error };
};

export default useLogo;
